import React, { useEffect, useState } from 'react';
import { Printer, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { dossierAPI } from '@/utils/api';

/**
 * Courrier de demande de pièces — livrable payant (version B) + génération admin.
 *
 * Récupère le courrier généré côté serveur (POST /dossiers/:id/letter/demande-pieces),
 * l'affiche dans un aperçu éditable (l'utilisateur complète les mentions entre crochets)
 * puis l'imprime. « Il n'a plus qu'à poster. »
 *
 * Impression : on écrit le courrier seul dans une iframe isolée puis on l'imprime.
 * (L'ancienne approche `visibility:hidden` laissait la hauteur des autres éléments
 * dans le flux → des dizaines de pages blanches.)
 *
 * Props :
 *   - dossierId : id du dossier
 *   - force     : régénère en ignorant le cache serveur (usage admin / itération prompt)
 */
const esc = (s) =>
  String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
  );

const DemandePiecesLetter = ({ dossierId, force = false }) => {
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Champs éditables (préremplis avec ce que le serveur a pu extraire des documents)
  const [expediteur, setExpediteur] = useState('');
  const [destAdresse, setDestAdresse] = useState('');
  const [lieuDate, setLieuDate] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await dossierAPI.getDemandePieces(dossierId, force);
        if (!active) return;
        setLetter(data);
        setExpediteur(data.expediteur || '');
        setDestAdresse((data.destinataire && data.destinataire.adresse) || '');
        setLieuDate(data.lieu_date || '');
      } catch (e) {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [dossierId, force]);

  if (loading) {
    return (
      <div className="card p-8 mb-8 flex items-center justify-center gap-3 text-gray-600">
        <Loader2 className="animate-spin" size={20} />
        Préparation de votre courrier de demande de pièces...
      </div>
    );
  }

  if (error || !letter) {
    return (
      <div className="card p-8 mb-8 text-center text-gray-600">
        Votre courrier n'a pas pu être généré pour le moment. Réessayez dans quelques instants.
      </div>
    );
  }

  const destNom = (letter.destinataire && letter.destinataire.nom) || '[Nom du créancier]';
  const corps = Array.isArray(letter.corps_paragraphes) ? letter.corps_paragraphes : [];
  const pieces = Array.isArray(letter.pieces_demandees) ? letter.pieces_demandees : [];

  const buildLetterHtml = () => {
    const nl2br = (s) => esc(s).replace(/\n/g, '<br>');
    const paras = corps
      .map((p) => `<p style="margin:0 0 12px;text-align:justify">${esc(p)}</p>`)
      .join('');
    const items = pieces.map((p) => `<li style="margin-bottom:4px">${esc(p)}</li>`).join('');
    const formule =
      letter.formule_politesse ||
      "Dans l'attente de votre réponse, je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations distinguées.";
    return `<!doctype html><html lang="fr"><head><meta charset="utf-8">
<title>Courrier de demande de pièces</title>
<style>
  @page { size: A4; margin: 2cm; }
  body { font-family: Georgia, 'Times New Roman', serif; color:#111; font-size:12pt; line-height:1.5; margin:0; }
  .exp { white-space:pre-line; margin-bottom:32px; }
  .dest { text-align:right; white-space:pre-line; margin-bottom:32px; }
  .date { text-align:right; margin-bottom:32px; }
  .lrar { font-weight:bold; margin-bottom:16px; }
  .objet { font-weight:bold; margin-bottom:24px; }
  ul { padding-left:24px; margin:0 0 12px; }
  .sign { margin-top:32px; }
  .disc { margin-top:40px; padding-top:8px; border-top:1px solid #ccc; font-size:9pt; color:#888; font-style:italic; }
</style></head><body>
  <div class="exp">${nl2br(expediteur || '[Vos nom et adresse]')}</div>
  <div class="dest">${nl2br(destNom + '\n' + (destAdresse || '[Adresse du créancier]'))}</div>
  <div class="date">${esc(lieuDate || '[Lieu], le [date]')}</div>
  ${letter.mention_lrar ? `<div class="lrar">${esc(letter.mention_lrar)}</div>` : ''}
  <div class="objet">Objet : ${esc(letter.objet || 'Demande de communication de pièces justificatives')}</div>
  <p style="margin:0 0 16px">Madame, Monsieur,</p>
  ${paras}
  ${items ? `<ul>${items}</ul>` : ''}
  ${
    letter.delai_reponse
      ? `<p style="margin:0 0 12px;text-align:justify">Je vous remercie de bien vouloir me communiquer ces éléments ${esc(
          letter.delai_reponse
        )}.</p>`
      : ''
  }
  <p style="margin:0 0 32px;text-align:justify">${esc(formule)}</p>
  <div class="sign">Signature :</div>
  ${letter.disclaimer ? `<div class="disc">${esc(letter.disclaimer)}</div>` : ''}
</body></html>`;
  };

  const handlePrint = () => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cw = iframe.contentWindow;
    const doc = cw.document;
    doc.open();
    doc.write(buildLetterHtml());
    doc.close();

    const cleanup = () => {
      setTimeout(() => {
        try {
          document.body.removeChild(iframe);
        } catch (e) {
          /* déjà retirée */
        }
      }, 500);
    };
    cw.onafterprint = cleanup;

    // Laisser le layout de l'iframe se calculer avant d'imprimer
    setTimeout(() => {
      cw.focus();
      cw.print();
    }, 200);
  };

  return (
    <div className="card p-6 sm:p-8 mb-8">
      {/* En-tête livrable */}
      <div className="mb-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-2" style={{ color: '#1e3a8a' }}>
          <FileText size={22} />
          <h3 className="text-2xl font-bold">Votre courrier de demande de pièces est prêt</h3>
        </div>
        <p className="text-gray-600">
          Complétez les mentions entre crochets si besoin, imprimez, et envoyez-le en recommandé avec accusé de réception.
        </p>
      </div>

      {/* Champs à compléter */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6 p-4 bg-blue-50 rounded-lg">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Vos coordonnées (expéditeur)</span>
          <textarea
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
            rows={3}
            value={expediteur}
            onChange={(e) => setExpediteur(e.target.value)}
            placeholder="Nom, prénom, adresse postale"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Adresse du créancier</span>
          <textarea
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
            rows={3}
            value={destAdresse}
            onChange={(e) => setDestAdresse(e.target.value)}
            placeholder="Adresse du créancier / société de recouvrement"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-sm font-medium text-gray-700">Lieu et date</span>
          <input
            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
            value={lieuDate}
            onChange={(e) => setLieuDate(e.target.value)}
            placeholder="Ville, le JJ/MM/AAAA"
          />
        </label>
      </div>

      {/* Aperçu du courrier */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-10 text-gray-900 leading-relaxed">
        <div className="whitespace-pre-line mb-8">{expediteur || '[Vos nom et adresse]'}</div>

        <div className="text-right whitespace-pre-line mb-8">
          {destNom}
          {'\n'}
          {destAdresse || '[Adresse du créancier]'}
        </div>

        <div className="text-right mb-8">{lieuDate || '[Lieu], le [date]'}</div>

        {letter.mention_lrar && <div className="font-semibold mb-4">{letter.mention_lrar}</div>}

        <div className="font-semibold mb-6">
          Objet : {letter.objet || 'Demande de communication de pièces justificatives'}
        </div>

        <div className="mb-4">Madame, Monsieur,</div>

        {corps.map((p, i) => (
          <p key={i} className="mb-4 text-justify">
            {p}
          </p>
        ))}

        {pieces.length > 0 && (
          <ul className="list-disc pl-6 mb-4">
            {pieces.map((p, i) => (
              <li key={i} className="mb-1">
                {p}
              </li>
            ))}
          </ul>
        )}

        {letter.delai_reponse && (
          <p className="mb-4 text-justify">
            Je vous remercie de bien vouloir me communiquer ces éléments {letter.delai_reponse}.
          </p>
        )}

        <p className="mb-8 text-justify">
          {letter.formule_politesse ||
            "Dans l'attente de votre réponse, je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations distinguées."}
        </p>

        <div className="mb-2">Signature :</div>
        <div className="h-16 border-b border-gray-200 w-1/2" />

        {letter.disclaimer && (
          <p className="mt-8 pt-4 border-t border-gray-100 text-xs text-gray-400 italic">
            {letter.disclaimer}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="mt-6 text-center">
        <Button
          size="lg"
          onClick={handlePrint}
          className="px-8 py-6 text-lg font-semibold"
          style={{ backgroundColor: '#1e3a8a', color: 'white' }}
        >
          <Printer className="mr-2" size={20} />
          Imprimer / Télécharger le courrier
        </Button>
      </div>
    </div>
  );
};

export default DemandePiecesLetter;
